import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, CheckCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function SurveyModal({ trigger }) {
  const [open, setOpen] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const fetchSurvey = useCallback(async () => {
    try {
      setLoading(true);
      setSubmitted(false);
      setAnswers({});
      setComment('');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/surveys/next`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurvey(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to share your experience');
      } else if (error.response?.status === 403) {
        toast.error('Surveys are for individual users only');
      } else if (error.response?.status === 404) {
        toast.info('No surveys available right now');
      }
      setSurvey(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !submitted) {
      fetchSurvey();
    }
  }, [open, submitted, fetchSurvey]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!survey) return;
    
    // Validate all 5 questions answered
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < 5) {
      toast.error(`Please answer all 5 questions (${answeredCount}/5 answered)`);
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/api/surveys/response`, {
        survey_key: survey.survey_key,
        answers: answers,
        comment: comment.trim() || null,
        context_route: window.location.pathname
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubmitted(true);
      toast.success('Thank you for sharing your experience!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Could not submit survey');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset state after close animation
    setTimeout(() => {
      setSurvey(null);
      setAnswers({});
      setComment('');
      setSubmitted(false);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            Share Your Experience
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-500" />
            {submitted ? 'Thank You!' : (survey?.title || 'Share Your Experience')}
          </DialogTitle>
        </DialogHeader>

        {/* Loading state */}
        {loading && (
          <div className="py-12 text-center text-slate-500">
            Loading survey...
          </div>
        )}

        {/* Submitted state */}
        {submitted && (
          <div className="py-12 text-center">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Thanks for your feedback!
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Your input helps us build a better community.
            </p>
            <Button onClick={handleClose} variant="outline">
              Close
            </Button>
          </div>
        )}

        {/* Survey form */}
        {!loading && !submitted && survey && (
          <div className="space-y-6">
            {survey.questions.map((question, idx) => (
              <div key={question.question_id} className="space-y-3">
                <p className="text-sm font-medium text-slate-900">
                  {idx + 1}. {question.prompt}
                </p>
                <RadioGroup
                  value={answers[question.question_id] || ''}
                  onValueChange={(value) => handleAnswerChange(question.question_id, value)}
                  className="space-y-2"
                >
                  {question.options.map((option, optIdx) => (
                    <div key={optIdx} className="flex items-center space-x-2">
                      <RadioGroupItem 
                        value={option} 
                        id={`${question.question_id}-${optIdx}`}
                        className="border-slate-300"
                      />
                      <Label 
                        htmlFor={`${question.question_id}-${optIdx}`}
                        className="text-sm text-slate-600 cursor-pointer"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}

            {/* Optional comment */}
            <div className="space-y-2 pt-4 border-t border-slate-100">
              <Label className="text-sm text-slate-700">
                Any additional thoughts? (optional)
              </Label>
              <Textarea
                placeholder="Share anything else on your mind..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting || Object.keys(answers).length < 5}
                className="bg-indigo-500 hover:bg-indigo-600"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </div>
          </div>
        )}

        {/* No survey available */}
        {!loading && !submitted && !survey && (
          <div className="py-12 text-center text-slate-500">
            <p>No surveys available right now.</p>
            <p className="text-sm mt-1">Check back later!</p>
            <Button onClick={handleClose} variant="outline" className="mt-4">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
